import { createReference } from "@medplum/core";
import { Patient, QuestionnaireResponse } from "@medplum/fhirtypes";
import type { LabOrderInputErrors, TestCoding } from "@medplum/health-gorilla-core";
import { useHealthGorillaLabOrderContext } from "@medplum/health-gorilla-react";
import type { TestMetadata } from "@medplum/health-gorilla-react";
import { QuestionnaireForm } from "@medplum/react";

import { ActionIcon, Card, Group, Radio, Stack, Text, Textarea } from "@mantine/core";

import { IconTrash } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";

import { AOEStatus, Priority } from "../types/labs";

interface LabTestCardProps {
  labTest: TestCoding;
  metadata: TestMetadata | undefined;
  patient: Patient | undefined;
  error?: NonNullable<LabOrderInputErrors["testMetadata"]>[keyof NonNullable<
    LabOrderInputErrors["testMetadata"]
  >];
  onRemove: (labTest: TestCoding) => void;
}

export function LabTestCard({ labTest, metadata, error, patient, onRemove }: LabTestCardProps) {
  const { updateTestMetadata } = useHealthGorillaLabOrderContext();

  if (!metadata) {
    return null;
  }

  const handlePriorityChange = (value: string) => {
    if (!value) {
      console.warn("New value for priority unexpectedly falsey", value);
      return;
    }
    const priority = value as TestMetadata["priority"];
    updateTestMetadata(labTest, { priority });
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value: notes } = event.currentTarget;
    updateTestMetadata(labTest, { notes });
  };

  const handleAOEResponsesChange = (response: QuestionnaireResponse) => {
    if (!response.subject?.reference && patient?.id) {
      response.subject = createReference(patient);
    }
    updateTestMetadata(labTest, { aoeResponses: response });
  };

  return (
    <Card
      p="lg"
      withBorder
      shadow="none"
      key={labTest.code}
      style={{ borderColor: "var(--mantine-color-cream-4)" }}
    >
      <Stack gap="lg">
        <Group justify="space-between">
          <TextWrapper variant="bold">{labTest.display}</TextWrapper>
          <ActionIcon
            c="red.7"
            variant="transparent"
            aria-label="Remove Lab Test"
            onClick={() => onRemove(labTest)}
          >
            <IconTrash size={24} />
          </ActionIcon>
        </Group>
        <Radio.Group
          withAsterisk
          label="Priority"
          value={metadata.priority}
          defaultValue={Priority.Routine}
          onChange={handlePriorityChange}
          error={error?.priority?.message}
        >
          <Stack gap="xs">
            <Radio mt="xs" label="Routine" value={Priority.Routine} />
            <Radio label="STAT" value={Priority.STAT} />
          </Stack>
        </Radio.Group>
        <Textarea
          label="Notes"
          placeholder="Test notes"
          value={metadata.notes ?? ""}
          onChange={handleNotesChange}
        />
        {metadata.aoeStatus === AOEStatus.Loading && <Loading size="sm" height="xl" />}
        {metadata.aoeStatus === AOEStatus.Error && <Text>Error fetching AoE</Text>}
        {metadata.aoeStatus === AOEStatus.Loaded && metadata.aoeQuestionnaire && (
          <>
            {error?.aoeResponses?.message && (
              <TextWrapper c="red">{error.aoeResponses.message}</TextWrapper>
            )}
            <QuestionnaireForm
              excludeButtons
              disablePagination
              onChange={handleAOEResponsesChange}
              questionnaire={metadata.aoeQuestionnaire}
            />
          </>
        )}
      </Stack>
    </Card>
  );
}
